import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useState } from "react";
import { Apple, Eye, EyeOff } from "lucide-react";
import landscape from "../../../../assets/images/landscape.jpg";
import food from "../../../../assets/images/food.jpg";
import kleidologo from "../../../../assets/images/Kleido.png";
import "./Signin.css"
// import { 
//     loginUser, 
//     validateLoginData, 
//     isUserAuthenticated,
//     clearAuthData 
// } from "../../../api/auth/userLogin";
import ForgotPasswordModal from "../../../common/ForgotPassword/ForgotPasswordModal";

function Counter({ to, prefix = "", suffix = "" }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString());

  useEffect(() => {
    const controls = animate(mv, to, { duration: 2.2, ease: "easeOut" });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [to, mv, rounded]);
  return (
    <span>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

function MiniGraph({ color }) {
  return (
    <svg viewBox="0 0 200 60" className="h-14 w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`g-${color}`} x1="0" y1="0" x2="0" y2="60" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d="M 0 50 L 20 42 L 40 46 L 60 34 L 80 38 L 100 28 L 120 30 L 140 20 L 160 24 L 180 12 L 200 8 L 200 60 L 0 60 Z"
        fill={`url(#g-${color})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <motion.path
        d="M 0 50 L 20 42 L 40 46 L 60 34 L 80 38 L 100 28 L 120 30 L 140 20 L 160 24 L 180 12 L 200 8"
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.8, ease: "easeInOut" }}
      />
    </svg>
  );
}

function ContentCard({
  image,
  revenue,
  uses,
  color,
  delay,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay }}
      className="card-elev overflow-hidden rounded-2xl"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.8))" }} />
        <div className="absolute bottom-3 left-3 rounded-full border border-white/15 bg-white/40 px-2.5 py-1 text-[10px] uppercase tracking-widest text-secondary backdrop-blur">
          Licensed content
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 p-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-2">Revenue</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            <Counter to={revenue} prefix="$" />
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-2">Licensed uses</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            <Counter to={uses} suffix="+" />
          </div>
        </div>
        <div className="col-span-2">
          <MiniGraph color={color} />
        </div>
      </div>
    </motion.div>
  );
}

const flow = ["Posts", "Consent", "Share", "Like", "comment", "Review"];

export function SignInPage() {
  const [showPass, setShowPass] = useState(false);
    const [loginData, setLoginData] = useState({
      identifier: '',
      password: ''
    });

    // const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isForgotPasswordOpen, setForgotPasswordOpen] = useState(false);

    const navigate = useNavigate();
    
      // ✅ Ensure default user is always present in localStorage
      useEffect(() => {
        let users = JSON.parse(localStorage.getItem("users")) || [];
        const exists = users.some((u) => u.userId === loginData.identifier);
    
        if (!exists) {
          users.push(loginData);
          localStorage.setItem("users", JSON.stringify(users));
        }
      }, []);
    
      useEffect(() => {
        const loggedInString = localStorage.getItem("userLoggedin");
        if (loggedInString) {
          try {
            const loggedIn = JSON.parse(loggedInString);
            if (loggedIn?.userId) {
              console.log("Logged in: ", loggedIn.userId);
              navigate(`/user-feed/${loggedIn.userId}`);
            }
          } catch (error) {
            console.error("Error parsing user data:", error);
            // Optionally clear corrupted data
            localStorage.removeItem("userLoggedin");
          }
        }
      }, [navigate]);
    
      
    // ✅ Handle input changes
    const handleChange = (e) => {
        setLoginData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));    
        // Clear error when user starts typing
        if (errorMessage) setErrorMessage("");
    };
        
    const togglePasswordVisibility = () => {
        setShowPass(!showPass);
    };  

    // ✅ Handle login with new service
    const handleLogin = (e) => {
        e.preventDefault();
    
        const users = JSON.parse(localStorage.getItem("users")) || [];
        const { identifier, password } = loginData;
    
        const user = users.find(
          (u) =>
            (u.userId === identifier ||
              u.email === identifier ||
              u.mobile === identifier) &&
            u.password === password
        );
    
        if (user) {
          user.lastLogin = new Date().toISOString();
          localStorage.setItem("users", JSON.stringify(users));
    
          localStorage.setItem(
            "userLoggedin",
            JSON.stringify({ userId: user.userId, password: user.password })
          );
    
          navigate(`/user-feed/${user.userId}`);
        } else {
          setErrorMessage("Invalid credentials. Please try again.");
        }
      };
  
    return (
    <main className="bg-page min-h-screen w-full">
      <div className="mx-auto grid min-h-screen w-full max-w-[2000px] grid-cols-1 lg:grid-cols-[6fr_4fr]" style={{ width: "100vw"}}>
        {/* LEFT */}
        <div className="relative hidden flex-col justify-between px-10 py-10 lg:flex xl:px-16" style={{ paddingTop: 0}}>
          <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden style={{marginTop: -70 }}/>
          <Link to="/" className="relative z-10 w-fit">
          <img
            src={kleidologo}
            alt="Kleido logo"
            className="h-40 w-40 rounded-lg object-contain"
          />
          </Link>

          <div className="relative z-10 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ContentCard image={landscape} revenue={4832} uses={14000} color="#4f8dfd" delay={0.1} />
            <ContentCard image={food} revenue={2761} uses={12000} color="#a06bff" delay={0.25} />
          </div>

          <div className="relative z-10">
            <div className="text-xs uppercase tracking-widest text-muted-2">Monetised Actions</div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {flow.map((step, i) => (
                <div key={step} className="flex items-center gap-3">
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                    className="rounded-full border border-white/10 bg-white/[0.02] px-4 py-1.5 text-sm text-secondary"
                    style={{color: "white"}}
                  >
                    {step}
                  </motion.div>
                  {i < flow.length - 1 && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.45 + i * 0.1 }}
                      className="text-muted-2"
                    >
                      →
                    </motion.span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex items-center justify-center px-6 py-10 sm:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="card-elev w-full max-w-md rounded-3xl p-8 sm:p-10"
            style={{ boxShadow: "0 60px 120px -30px rgba(0,0,0,0.9)" }}
          >
            <h1
              className="mt-8 text-3xl font-semibold tracking-tight sm:text-4xl"
              style={{ letterSpacing: "-0.03em" }}
            >
              Welcome back.
            </h1>
            <p className="mt-2 text-sm text-muted-2">Sign in to continue to Kleido AI.</p>

            <form
              className="mt-8 space-y-4"
              onSubmit={handleLogin}
            >
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-2 text-black">UserID / Email / Mobile</label>
                <input
                type="text"
                name="identifier"
                placeholder="UserID / Email / Mobile"
                value={loginData.identifier}
                onChange={handleChange}
                disabled={isLoading}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-white/25 focus:border-white/40"
                required
              />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-widest text-muted-2">Password</label>
                  <p
                    className="text-xs text-secondary transition-colors hover:text-white"
                    onClick={() => setForgotPasswordOpen(true)}
                    style={{marginTop: 10, color: "gray"}}
                  >
                    Forgot Password?
                  </p>

                  {/* Forgot Password Modal */}
                  {isForgotPasswordOpen && (
                    <ForgotPasswordModal
                        forgotPasswordType="mobileNumber"
                        close={() => setForgotPasswordOpen(false)}
                    />
                  )}
                </div>
                <div className="relative mt-2">
                <input
                  type={showPass ? "text" : "password"}
                  name="password"
                //   placeholder="Password"
                  value={loginData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  style={{color: "black", height: 50}}
                  required
                />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-2 transition-colors hover:text-white"
                    aria-label="Toggle password visibility"
                    style={{color: "black"}}
                  >
                    {showPass ? <EyeOff className="h-4 w-4 text-black" /> : <Eye className="h-4 w-4 text-black" />}
                  </button>
                </div>
              </div>

              <button 
              type="submit" 
              disabled={isLoading}
              className="btn-primary w-full"
              style={{
                background: isLoading ? "black" : "rgb(34, 31, 31)",
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
                padding: "0.9rem 1.6rem",
                marginTop: 30
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
            </form>

            <label className="form-group" style={{ display: "flex", flexDirection: "row" }}>
                <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{ marginTop: 10 }}
                    className="text-xs text-secondary transition-colors hover:text-white"
                />
                <p className="text-xs text-secondary transition-colors" style={{ marginLeft: 10, marginTop: 10, color: "gray" }}>
                    Remember Me
                </p>
            </label>

            {/* Forgot Password Modal */}
            {isForgotPasswordOpen && (
                <ForgotPasswordModal
                    forgotPasswordType="mobileNumber"
                    close={() => setForgotPasswordOpen(false)}
                />
            )}

            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-2">
              <div className="h-px flex-1 bg-white/10" />
              or
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <div className="space-y-2.5">
              <button className="btn-secondary btn-secondary-hover flex w-full items-center justify-center gap-3 rounded-xl">
                <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden>
                  <path fill="#EA4335" d="M24 9.5c3.3 0 6.2 1.1 8.5 3.3l6.4-6.4C34.7 2.7 29.8 1 24 1 14.9 1 7.1 6.3 3.4 14l7.5 5.8C12.6 13.7 17.8 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.5 5.8c4.4-4 7.2-10.1 7.2-17.5z" />
                  <path fill="#FBBC05" d="M10.9 28.2c-.5-1.4-.8-2.9-.8-4.2s.3-2.8.8-4.2l-7.5-5.8C1.9 17 1 20.4 1 24s.9 7 2.4 10l7.5-5.8z" />
                  <path fill="#34A853" d="M24 47c6.5 0 11.9-2.1 15.8-5.8l-7.5-5.8c-2.1 1.4-4.8 2.2-8.3 2.2-6.2 0-11.4-4.2-13.2-9.8l-7.5 5.8C7.1 41.7 14.9 47 24 47z" />
                </svg>
                Continue with Google
              </button>
              <button className="btn-secondary btn-secondary-hover flex w-full items-center justify-center gap-3 rounded-xl">
                <Apple className="h-4 w-4" />
                Continue with Apple
              </button>
            </div>

            {/* <StoreLinks/> */}

            <div className="mt-6 text-center text-sm text-muted-2" style={{ height: "30px"}}>
              Don't have an account?{" "}
              <a href="/signup" className="text-white underline-offset-4 hover:underline">
                Sign Up
              </a>
              <div className="mt-4 flex flex-col items-center text-center text-xs text-muted-2">
                <div style={{color: "white", fontSize: 10}}>© {new Date().getFullYear()} Initiumnet, A human intelligence data company </div>
            </div>
            </div>
          </motion.div>
        </div>
      </div>

    </main>
  );
}
